// src/pages/admin/AdminSocialLinks.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  getSocialLinks,
  addSocialLink,
  updateSocialLink,
  deleteSocialLink,
} from "../../services/firestoreService";
import { SOCIAL_PLATFORMS, getPlatformIcon, getPlatformLabel } from "../../constants/socialPlatforms";
import Modal from "../../components/common/Modal";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiArrowUp, FiArrowDown } from "react-icons/fi";

export default function AdminSocialLinks() {
  const [links,      setLinks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [reordering, setReordering] = useState(false);

  const {
    register, handleSubmit, control, watch, setValue, reset,
    formState: { errors },
  } = useForm({ defaultValues: { platform: "facebook", label: "", href: "" } });

  const platform = watch("platform");

  const load = async () => {
    const data = await getSocialLinks();
    setLinks(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    reset({ platform: "facebook", label: getPlatformLabel("facebook"), href: "" });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    reset({
      platform: item.platform || "facebook",
      label: item.label || "",
      href: item.href || "",
    });
    setModalOpen(true);
  };

  // Auto-fill the label whenever the platform changes, unless the admin has
  // already typed a custom label for a NEW link (edits keep whatever's saved).
  const handlePlatformChange = (value) => {
    setValue("platform", value);
    if (!editing) setValue("label", getPlatformLabel(value));
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      if (editing) {
        await updateSocialLink(editing.id, data);
        toast.success("Link updated");
      } else {
        const nextOrder = links.length
          ? Math.max(...links.map((l) => l.order ?? 0)) + 1
          : 0;
        await addSocialLink({ ...data, order: nextOrder });
        toast.success("Link added");
      }
      setModalOpen(false);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Remove "${label}" from the footer?`)) return;
    await deleteSocialLink(id);
    toast.success("Link removed");
    setLinks((prev) => prev.filter((l) => l.id !== id));
  };

  // Swap the `order` value of two adjacent links and persist both
  const move = async (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= links.length) return;

    setReordering(true);
    try {
      const current = links[index];
      const target  = links[targetIndex];
      const currentOrder = current.order ?? index;
      const targetOrder  = target.order ?? targetIndex;

      await Promise.all([
        updateSocialLink(current.id, { order: targetOrder }),
        updateSocialLink(target.id, { order: currentOrder }),
      ]);

      const next = [...links];
      next[index] = { ...target, order: currentOrder };
      next[targetIndex] = { ...current, order: targetOrder };
      setLinks(next);
    } catch {
      toast.error("Reorder failed");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Follow Us</h1>
          {/* <p className="text-sm text-gray-500 mt-1">
            Manage the social links shown in the footer's "Follow Us" section.
          </p> */}
        </div>
        <button onClick={openAdd} className="btn-primary text-[13px] sm:text-md flex items-center gap-2">
          <FiPlus className="w-4 h-4" /> New Link
        </button>
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Order", "Platform", "Label", "URL", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {links.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-500">
                    No social links yet. Add your first one!
                  </td>
                </tr>
              ) : (
                links.map((item, index) => {
                  const Icon = getPlatformIcon(item.platform);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            disabled={reordering || index === 0}
                            onClick={() => move(index, "up")}
                            className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <FiArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            disabled={reordering || index === links.length - 1}
                            onClick={() => move(index, "down")}
                            className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <FiArrowDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{item.label}</td>
                      <td className="px-4 py-3 text-gray-500 max-w-xs">
                        <a href={item.href} target="_blank" rel="noopener noreferrer" className="truncate block hover:text-primary-600">
                          {item.href}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors">
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(item.id, item.label)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Link" : "New Link"}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Platform *</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {SOCIAL_PLATFORMS.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handlePlatformChange(value)}
                  className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border text-xs font-medium transition-colors
                    ${platform === value
                      ? "bg-primary-50 border-primary-400 text-primary-700"
                      : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"}`}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                  <span className="truncate w-full text-center">{label}</span>
                </button>
              ))}
            </div>
            {/* Keeps react-hook-form aware of the value even though selection
                happens via the buttons above rather than a native input */}
            <input type="hidden" {...register("platform", { required: true })} />
          </div>

          <div>
            <label className="label">Label *</label>
            <input
              {...register("label", { required: "Label is required" })}
              className={`input ${errors.label ? "border-red-400" : ""}`}
              placeholder="e.g. Facebook"
            />
            {errors.label && <p className="text-xs text-red-500 mt-1">{errors.label.message}</p>}
          </div>

          <div>
            <label className="label">Profile URL *</label>
            <input
              {...register("href", {
                required: "URL is required",
                pattern: { value: /^https?:\/\/.+/i, message: "Must start with http:// or https://" },
              })}
              className={`input ${errors.href ? "border-red-400" : ""}`}
              placeholder="https://facebook.com/tripuraagro"
            />
            {errors.href && <p className="text-xs text-red-500 mt-1">{errors.href.message}</p>}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? <><Spinner size="sm" /> Saving...</> : editing ? "Update Link" : "Add Link"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}